import React from 'react';

export interface MobileCardProps {
  children: React.ReactNode;
  header?: React.ReactNode;
  footer?: React.ReactNode;
  onClick?: () => void;
  className?: string;
}

/**
 * MobileCard Component v2 - FacturXPro Design System
 *
 * Carte optimisée pour mobile avec header et footer optionnels
 * Utilisée dans les listes mobiles (InvoicesListResponsive, QuotesListResponsive)
 *
 * @example
 * ```tsx
 * <MobileCard
 *   header={
 *     <div className="flex justify-between items-center">
 *       <Badge variant="success">Payé</Badge>
 *       <span className="text-sm text-gray-500">Il y a 2h</span>
 *     </div>
 *   }
 *   footer={
 *     <div className="flex gap-2">
 *       <Button variant="ghost" size="sm">Voir</Button>
 *       <Button variant="secondary" size="sm">Modifier</Button>
 *     </div>
 *   }
 *   onClick={() => navigate(`/invoices/${invoice.id}`)}
 * >
 *   <h3 className="font-bold text-lg">Facture #1234</h3>
 *   <p className="text-gray-600">Client XYZ</p>
 *   <p className="text-2xl font-bold text-teal-600">€1,250</p>
 * </MobileCard>
 * ```
 *
 * Référence: DESIGN_SYSTEM.md section "Mobile Card"
 */
export const MobileCard: React.FC<MobileCardProps> = ({
  children,
  header,
  footer,
  onClick,
  className = '',
}) => {
  return (
    <div
      className={`
        bg-white/20
        backdrop-blur-xl
        border
        border-white/30
        rounded-2xl
        shadow-lg
        shadow-gray-300/10
        overflow-hidden
        transition-all
        duration-300
        ${onClick ? 'cursor-pointer hover:shadow-xl hover:-translate-y-0.5' : ''}
        ${className}
      `.trim()}
      onClick={onClick}
    >
      {header && (
        <div className="px-4 py-3 border-b border-gray-200/30">{header}</div>
      )}

      <div className="px-4 py-4">{children}</div>

      {footer && (
        <div className="px-4 py-3 border-t border-gray-200/30 bg-white/20">
          {footer}
        </div>
      )}
    </div>
  );
};

export default MobileCard;
